import React, { useEffect, useRef, useState } from "react";
import { Alert, Spin } from "antd";
import mermaid from "mermaid";
import {
  fetchProjectDocumentationFileBlob,
  parseMarkdownBlobForPreview,
} from "../../../app/utils/common/projectDocumentationFile";
import "./ProjectDocumentationMarkdownViewer.scss";

mermaid.initialize({
  startOnLoad: false,
  securityLevel: "strict",
  theme: "default",
});

const attachMermaidInlineError = (node, message) => {
  if (!node) return;
  const safeMessage = String(message || "Invalid Mermaid diagram syntax.");
  const wrapper = node.closest(".project-doc-mermaid-wrap");
  if (!wrapper) return;
  wrapper.classList.add("project-doc-mermaid-wrap--error");
  wrapper.setAttribute("data-mermaid-error", safeMessage);
};

/**
 * Read-only Markdown preview for project documentation.
 */
const ProjectDocumentationMarkdownViewer = ({ documentId, fileDocumentId, onLoadError, compact = false }) => {
  const [loading, setLoading] = useState(true);
  const [html, setHtml] = useState("");
  const [loadError, setLoadError] = useState(null);
  const contentRef = useRef(null);
  const onLoadErrorRef = useRef(onLoadError);
  onLoadErrorRef.current = onLoadError;

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setLoadError(null);
    setHtml("");

    (async () => {
      try {
        const blob = await fetchProjectDocumentationFileBlob(documentId, fileDocumentId);
        const rendered = await parseMarkdownBlobForPreview(blob);
        if (!cancelled) {
          setHtml(rendered);
        }
      } catch (e) {
        if (!cancelled) {
          setLoadError(e?.message || "Could not load the Markdown file.");
          onLoadErrorRef.current?.(e);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [documentId, fileDocumentId]);

  useEffect(() => {
    if (loading || loadError || !html || !contentRef.current) return;

    const nodes = Array.from(contentRef.current.querySelectorAll(".mermaid"));
    if (!nodes.length) return;

    nodes.forEach((node) => node.removeAttribute("data-processed"));
    Promise.allSettled(
      nodes.map((node) =>
        mermaid.run({ nodes: [node] }).catch((error) => {
          attachMermaidInlineError(node, error?.message);
          throw error;
        })
      )
    ).then((results) => {
      const failures = results.filter((result) => result.status === "rejected");
      if (failures.length) {
        const firstError = failures[0]?.reason;
        onLoadErrorRef.current?.(firstError);
      }
    });
  }, [html, loading, loadError]);

  if (loading) {
    return (
      <div className={`project-doc-markdown-viewer ${compact ? "project-doc-markdown-viewer--compact" : ""}`}>
        <div className="flex justify-center py-12">
          <Spin />
        </div>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className={`project-doc-markdown-viewer ${compact ? "project-doc-markdown-viewer--compact" : ""}`}>
        <Alert type="error" showIcon message={loadError} />
      </div>
    );
  }

  return (
    <div
      ref={contentRef}
      className={`project-doc-markdown-viewer ql-editor ${compact ? "project-doc-markdown-viewer--compact" : ""}`}
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
};

export default ProjectDocumentationMarkdownViewer;
